/* The replay viewer's model: where each entry sits on the timeline, which
   frame and markers show at any point of it, what a step did in words, and
   the GIF and video exports, which draw the same pictures onto a canvas. */

import { GifEncoder } from './gif';
import type { AgentVerdict, FrameEntry, Recording, RecordingEntry, RunFinishedEntry, StepEntry } from './types';

/** Playback shows an idle stretch longer than this (at 1×) at this length. */
export const MAX_GAP_MS = 2000;

export function frameUrl(recordingId: string, frame: string): string {
  return `/v1/recordings/${encodeURIComponent(recordingId)}/frames/${encodeURIComponent(frame)}`;
}

/** A frame entry, or a step that took a screenshot. */
export type FrameBearing = FrameEntry | (StepEntry & { frame: string });

export function hasFrame(entry: RecordingEntry): entry is FrameBearing {
  return (entry.type === 'frame' || entry.type === 'step') && typeof entry.frame === 'string' && entry.frame !== '';
}

/* ------------------------------------------------------------ timeline */

export interface Timeline {
  /** Each entry's time after the recording started. Never goes back: the box's and the plane's clocks differ a little. */
  offsets: number[];
  /** The same with every gap capped at MAX_GAP_MS: playback time at 1×. */
  packed: number[];
  /** For each entry, the latest frame-bearing entry at or before it, or -1. */
  frameAt: number[];
  /** The entries the side list shows: everything but bare frames. */
  listed: number[];
  /** For each entry, the latest listed entry at or before it, or -1. */
  listedAt: number[];
}

export function buildTimeline(entries: RecordingEntry[], startedAt: number): Timeline {
  const n = entries.length;
  const offsets = new Array<number>(n);
  const packed = new Array<number>(n);
  const frameAt = new Array<number>(n);
  const listedAt = new Array<number>(n);
  const listed: number[] = [];
  let t = 0;
  let c = 0;
  let frame = -1;
  let shown = -1;
  for (let i = 0; i < n; i++) {
    const e = entries[i];
    const at = Math.max(t, e.at - startedAt);
    c += Math.min(at - t, MAX_GAP_MS);
    t = at;
    offsets[i] = t;
    packed[i] = c;
    if (hasFrame(e)) frame = i;
    frameAt[i] = frame;
    if (e.type !== 'frame') {
      listed.push(i);
      shown = i;
    }
    listedAt[i] = shown;
  }
  return { offsets, packed, frameAt, listed, listedAt };
}

/** The timeline's length: to its end once ended, to now while it records, and never short of its last entry. */
export function timelineEnd(rec: Recording, tl: Timeline, now: number): number {
  const last = tl.offsets.length ? tl.offsets[tl.offsets.length - 1] : 0;
  const end = rec.state === 'ended' ? (rec.endedAt ?? rec.startedAt + last) : now;
  return Math.max(last, end - rec.startedAt);
}

/** The last index whose value is at most v, or -1. */
function lastAtMost(values: number[], v: number): number {
  let lo = 0;
  let hi = values.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (values[mid] <= v) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/** The entry showing at time t: the last at or before it, or -1 before the first. */
export function entryAt(tl: Timeline, t: number): number {
  return lastAtMost(tl.offsets, t);
}

/** The gap around position i (-1 is the stretch before the first entry, the last runs to `end`). */
function segment(tl: Timeline, i: number, end: number) {
  const n = tl.offsets.length;
  const t0 = i < 0 ? 0 : tl.offsets[i];
  const c0 = i < 0 ? 0 : tl.packed[i];
  const t1 = i + 1 < n ? tl.offsets[i + 1] : Math.max(end, t0);
  const c1 = i + 1 < n ? tl.packed[i + 1] : c0 + Math.min(t1 - t0, MAX_GAP_MS);
  return { t0, c0, gap: t1 - t0, packedGap: c1 - c0 };
}

/** Real time to playback time. */
export function toPacked(tl: Timeline, t: number, end: number): number {
  const { t0, c0, gap, packedGap } = segment(tl, lastAtMost(tl.offsets, t), end);
  return gap > 0 ? c0 + (Math.min(t - t0, gap) * packedGap) / gap : c0;
}

/** Playback time to real time. */
export function fromPacked(tl: Timeline, c: number, end: number): number {
  const { t0, c0, gap, packedGap } = segment(tl, lastAtMost(tl.packed, c), end);
  return packedGap > 0 ? t0 + (Math.min(c - c0, packedGap) * gap) / packedGap : t0;
}

/* --------------------------------------------------------------- steps */

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);

function point(v: unknown): [number, number] | null {
  return Array.isArray(v) && v.length === 2 && num(v[0]) !== undefined && num(v[1]) !== undefined ? [v[0] as number, v[1] as number] : null;
}
const at = (p: [number, number]) => `(${Math.round(p[0])}, ${Math.round(p[1])})`;

function quote(text: string, max = 64): string {
  const flat = text.replace(/\r?\n/g, '⏎');
  return `“${flat.length > max ? `${flat.slice(0, max - 1)}…` : flat}”`;
}

/** The computer-use member a step ran: the tool input's `action`, else the step's own. */
export function stepAction(step: StepEntry): string {
  return str(step.input?.action) ?? step.action;
}

/** What a step did, in a few words: click (412, 300), type “hello”, key cmd+s. */
export function stepLabel(step: StepEntry): string {
  const input = step.input ?? {};
  const action = stepAction(step);
  const where = point(input.coordinate);
  const place = where ? ` ${at(where)}` : '';
  // A click's `text` holds the modifiers held through it.
  const mods = str(input.text) ? `${input.text}+` : '';
  switch (action) {
    case 'screenshot':
      return 'screenshot';
    case 'left_click':
      return `${mods}click${place}`;
    case 'right_click':
      return `${mods}right-click${place}`;
    case 'middle_click':
      return `${mods}middle-click${place}`;
    case 'double_click':
      return `${mods}double-click${place}`;
    case 'triple_click':
      return `${mods}triple-click${place}`;
    case 'left_mouse_down':
      return `mouse down${place}`;
    case 'left_mouse_up':
      return `mouse up${place}`;
    case 'mouse_move':
      return `move to${place}`;
    case 'left_click_drag': {
      const from = point(input.start_coordinate);
      return from && where ? `drag ${at(from)} → ${at(where)}` : 'drag';
    }
    case 'scroll': {
      const dir = str(input.scroll_direction);
      const amount = num(input.scroll_amount);
      return `scroll${dir ? ` ${dir}` : ''}${amount !== undefined ? ` ${amount}` : ''}${where ? ` at ${at(where)}` : ''}`;
    }
    case 'type':
      return input.secret === true ? 'type (secret)' : typeof input.text === 'string' ? `type ${quote(input.text)}` : 'type';
    case 'key': {
      const repeat = num(input.repeat);
      return `key ${str(input.text) ?? ''}${repeat && repeat > 1 ? ` ×${repeat}` : ''}`.trim();
    }
    case 'hold_key':
      return `hold ${str(input.text) ?? 'a key'}${num(input.duration) !== undefined ? ` for ${input.duration} s` : ''}`;
    case 'wait':
      return num(input.duration) !== undefined ? `wait ${input.duration} s` : 'wait';
    case 'finish':
      return str(input.status) ? `finish: ${input.status}` : 'finish';
    default:
      return `${action.replace(/_/g, ' ')}${place}`;
  }
}

export function verdictLabel(run: RunFinishedEntry): string {
  if (run.verdict === 'done') return 'Agent done';
  if (run.verdict === 'failed') return 'Agent failed';
  if (run.verdict === 'incomplete') return 'Agent incomplete';
  return run.error ? 'Agent stopped' : 'Agent finished';
}

/** One line for any entry, as the scrubber's "now" and the exports' caption say it; null for frames, logs and the end. */
export function caption(entry: RecordingEntry): string | null {
  switch (entry.type) {
    case 'step':
      return stepLabel(entry);
    case 'run':
      return entry.event === 'started' ? `Goal: ${entry.goal}` : verdictLabel(entry);
    case 'command':
      return `$ ${entry.command}`;
    default:
      return null;
  }
}

export interface RunSummary {
  runs: number;
  /** A run started and hasn't finished. */
  running: boolean;
  /** The latest finished run. */
  last?: RunFinishedEntry;
}

export function runSummary(entries: RecordingEntry[]): RunSummary {
  const open = new Set<string>();
  let runs = 0;
  let last: RunFinishedEntry | undefined;
  for (const e of entries) {
    if (e.type !== 'run') continue;
    if (e.event === 'started') {
      runs++;
      open.add(e.runId);
    } else {
      open.delete(e.runId);
      last = e;
    }
  }
  return { runs, running: open.size > 0, last };
}

export const VERDICT_TONE: Record<AgentVerdict | 'error', 'accent' | 'refused' | 'magenta'> = {
  done: 'accent',
  failed: 'refused',
  incomplete: 'magenta',
  error: 'refused',
};

/* ------------------------------------------------------------- markers */

export interface Mark {
  kind: 'click' | 'move' | 'drag' | 'scroll';
  /** Where, as fractions of the frame's width and height. */
  x: number;
  y: number;
  /** A drag's end. */
  x2?: number;
  y2?: number;
  dir?: 'up' | 'down' | 'left' | 'right';
  /** A click's count (1 to 3), and its button. */
  count?: number;
  button?: 'left' | 'right' | 'middle';
  ok: boolean;
  /** The step showing now, rather than an earlier one drawn on the same frame. */
  current: boolean;
}

/** A step's marker on a frame of the given size, or null when it has no coordinates. */
function stepMark(step: StepEntry, frame: { width?: number; height?: number }, current: boolean): Mark | null {
  // Coordinates are in `space` pixels; without one, in the frame's own.
  const sw = step.space?.width ?? frame.width;
  const sh = step.space?.height ?? frame.height;
  if (!sw || !sh) return null;
  const input = step.input ?? {};
  const where = point(input.coordinate);
  const base = { ok: step.ok, current };
  // A point off the screen (a refused step's) is drawn at its edge.
  const fx = (p: [number, number]) => ({ x: Math.min(1, Math.max(0, p[0] / sw)), y: Math.min(1, Math.max(0, p[1] / sh)) });
  switch (stepAction(step)) {
    case 'left_click':
    case 'right_click':
    case 'middle_click':
    case 'double_click':
    case 'triple_click':
    case 'left_mouse_down':
    case 'left_mouse_up': {
      if (!where) return null;
      const action = stepAction(step);
      return {
        kind: 'click',
        ...fx(where),
        count: action === 'double_click' ? 2 : action === 'triple_click' ? 3 : 1,
        button: action === 'right_click' ? 'right' : action === 'middle_click' ? 'middle' : 'left',
        ...base,
      };
    }
    case 'mouse_move':
      return where ? { kind: 'move', ...fx(where), ...base } : null;
    case 'left_click_drag': {
      const from = point(input.start_coordinate);
      if (!from || !where) return null;
      const end = fx(where);
      return { kind: 'drag', ...fx(from), x2: end.x, y2: end.y, ...base };
    }
    case 'scroll': {
      const dir = input.scroll_direction;
      if (!where || (dir !== 'up' && dir !== 'down' && dir !== 'left' && dir !== 'right')) return null;
      return { kind: 'scroll', ...fx(where), dir, ...base };
    }
    default:
      return null;
  }
}

/** How many earlier markers stay on a frame, faded, under the current step's. */
const MARK_TRAIL = 5;

/** The markers on the frame showing at entry `index`: the steps with coordinates since that frame, the newest marked current. */
export function marksAt(entries: RecordingEntry[], tl: Timeline, index: number): Mark[] {
  if (index < 0) return [];
  const f = tl.frameAt[index];
  if (f < 0) return [];
  const frame = entries[f] as FrameBearing;
  const marks: Mark[] = [];
  for (let i = index; i >= f && marks.length <= MARK_TRAIL; i--) {
    const e = entries[i];
    if (e.type !== 'step') continue;
    const mark = stepMark(e, frame, i === index);
    if (mark) marks.unshift(mark);
  }
  return marks;
}

/* ------------------------------------------------------------- drawing */

/** The markers' colors, as --mark, --mark-fail, --mark-halo and --stage in styles.css; KEEP holds them exact in a GIF's palettes. */
const INK = { mark: '#f5a524', fail: '#ff6b5b', halo: 'rgba(15, 17, 21, 0.72)', back: '#0b0c0f' };
const KEEP: [number, number, number][] = [
  [245, 165, 36],
  [255, 107, 91],
  [255, 255, 255],
  [15, 17, 21],
];

export interface Picture {
  image: HTMLImageElement | null;
  marks: Mark[];
  caption: string | null;
}

/** One picture of the replay, as the viewer shows it: the frame fitted in, its markers and a caption. */
export function drawPicture(ctx: CanvasRenderingContext2D, w: number, h: number, pic: Picture): void {
  ctx.fillStyle = INK.back;
  ctx.fillRect(0, 0, w, h);
  const img = pic.image;
  if (!img || !img.naturalWidth) return;
  const s = Math.min(w / img.naturalWidth, h / img.naturalHeight);
  const dw = img.naturalWidth * s;
  const dh = img.naturalHeight * s;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, dx, dy, dw, dh);
  // Marker sizes match the viewer's at a 720 px wide frame.
  const u = Math.max(0.75, dw / 720);
  for (const m of pic.marks) drawMark(ctx, m, dx, dy, dw, dh, u);
  if (pic.caption) drawCaption(ctx, pic.caption, w, h, u);
}

function drawMark(ctx: CanvasRenderingContext2D, m: Mark, dx: number, dy: number, dw: number, dh: number, u: number): void {
  const x = dx + m.x * dw;
  const y = dy + m.y * dh;
  const color = m.ok ? INK.mark : INK.fail;
  const line = (path: () => void, width: number) => {
    ctx.beginPath();
    path();
    ctx.strokeStyle = INK.halo;
    ctx.lineWidth = width + 3 * u;
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.stroke();
  };
  const circle = (cx: number, cy: number, r: number) => () => ctx.arc(cx, cy, r, 0, Math.PI * 2);
  const dot = (cx: number, cy: number, r: number) => {
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 1.5 * u;
    ctx.strokeStyle = INK.halo;
    ctx.stroke();
  };
  const arrow = (x1: number, y1: number, x2: number, y2: number) => {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const head = 9 * u;
    line(() => {
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.moveTo(x2 - head * Math.cos(angle - 0.5), y2 - head * Math.sin(angle - 0.5));
      ctx.lineTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(angle + 0.5), y2 - head * Math.sin(angle + 0.5));
    }, 2.5 * u);
  };
  ctx.save();
  ctx.globalAlpha = m.current ? 1 : 0.45;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  switch (m.kind) {
    case 'click': {
      if (m.button === 'right') ctx.setLineDash([4 * u, 3 * u]);
      line(circle(x, y, 14 * u), 2.5 * u);
      for (let k = 2; k <= (m.count ?? 1); k++) line(circle(x, y, (14 + (k - 1) * 6) * u), 2 * u);
      ctx.setLineDash([]);
      dot(x, y, 3 * u);
      break;
    }
    case 'move':
      line(circle(x, y, 6 * u), 2 * u);
      break;
    case 'drag': {
      dot(x, y, 4 * u);
      arrow(x, y, dx + (m.x2 ?? m.x) * dw, dy + (m.y2 ?? m.y) * dh);
      break;
    }
    case 'scroll': {
      const [ux, uy] = DIRS[m.dir ?? 'down'];
      line(circle(x, y, 12 * u), 2.5 * u);
      arrow(x + ux * 16 * u, y + uy * 16 * u, x + ux * 34 * u, y + uy * 34 * u);
      break;
    }
  }
  ctx.restore();
}

export const DIRS: Record<NonNullable<Mark['dir']>, [number, number]> = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };

function drawCaption(ctx: CanvasRenderingContext2D, text: string, w: number, h: number, u: number): void {
  const size = Math.round(13 * u);
  const pad = Math.round(8 * u);
  const margin = Math.round(12 * u);
  ctx.save();
  ctx.font = `600 ${size}px 'Mona Sans', system-ui, -apple-system, 'Segoe UI', sans-serif`;
  ctx.textBaseline = 'middle';
  const room = w - margin * 2 - pad * 2;
  let shown = text;
  if (ctx.measureText(shown).width > room) {
    while (shown.length > 1 && ctx.measureText(`${shown}…`).width > room) shown = shown.slice(0, Math.max(1, Math.floor(shown.length * 0.9)));
    shown += '…';
  }
  const bw = ctx.measureText(shown).width + pad * 2;
  const bh = size + pad * 1.5;
  const by = h - margin - bh;
  ctx.fillStyle = 'rgba(15, 17, 21, 0.8)';
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(margin, by, bw, bh, 6 * u);
  else ctx.rect(margin, by, bw, bh);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(shown, margin + pad, by + bh / 2);
  ctx.restore();
}

/* ------------------------------------------------------------- exports */

/** One picture of an export: the timeline at entry `index`, held for `holdMs` of playback time. */
interface Shot {
  index: number;
  frame: string;
  at: number;
  holdMs: number;
}

/** Long enough to see a marker that the next screenshot replaced at once. */
const MIN_HOLD_MS = 250;
const LAST_HOLD_MS = 2000;
const GIF_SHOTS = 300;
const GIF_WIDTH = 960;
const VIDEO_SHOTS = 900;
const VIDEO_WIDTH = 1280;
/** A video plays a long run faster, so it records in about this long. */
const VIDEO_TARGET_MS = 55_000;

/**
 * The pictures an export shows: one wherever the frame, the markers or the
 * caption change, timed as playback times them, sampled evenly down to `max`.
 */
function shotsOf(rec: Recording, tl: Timeline, end: number, max: number): Shot[] {
  const all: Shot[] = [];
  rec.entries.forEach((e, i) => {
    const f = tl.frameAt[i];
    if (f < 0 || e.type === 'log' || e.type === 'end') return;
    all.push({ index: i, frame: (rec.entries[f] as FrameBearing).frame, at: tl.packed[i], holdMs: 0 });
  });
  const shots = all.length > max ? Array.from({ length: max }, (_, k) => all[Math.round((k * (all.length - 1)) / (max - 1))]) : all;
  const packedEnd = toPacked(tl, end, end);
  shots.forEach((shot, k) => {
    const next = shots[k + 1];
    shot.holdMs = next ? Math.max(MIN_HOLD_MS, next.at - shot.at) : Math.min(LAST_HOLD_MS, Math.max(MIN_HOLD_MS * 4, packedEnd - shot.at));
  });
  return shots;
}

function pictureOf(rec: Recording, tl: Timeline, shot: Shot, image: HTMLImageElement | null): Picture {
  const listed = tl.listedAt[shot.index];
  return { image, marks: marksAt(rec.entries, tl, shot.index), caption: listed >= 0 ? caption(rec.entries[listed]) : null };
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  return img.decode().then(
    () => img,
    () => null,
  );
}

/** Loads the shots' frames a few ahead of the one asked for. */
function frameLoader(recordingId: string, shots: Shot[], ahead = 4) {
  const loading = new Map<string, Promise<HTMLImageElement | null>>();
  return (k: number): Promise<HTMLImageElement | null> => {
    for (let j = k; j < Math.min(shots.length, k + ahead + 1); j++) {
      const name = shots[j].frame;
      if (!loading.has(name)) loading.set(name, loadImage(frameUrl(recordingId, name)));
    }
    // Frames behind us are done with.
    for (const name of loading.keys()) if (!shots.slice(k, k + ahead + 1).some((s) => s.frame === name)) loading.delete(name);
    return loading.get(shots[k].frame)!;
  };
}

const abortError = () => new DOMException('The export was cancelled.', 'AbortError');
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface ExportOptions {
  signal: AbortSignal;
  onProgress: (fraction: number) => void;
}

function canvasFor(firstImage: HTMLImageElement | null, maxWidth: number, even: boolean) {
  const nw = firstImage?.naturalWidth || 1280;
  const nh = firstImage?.naturalHeight || 800;
  let w = Math.min(maxWidth, nw);
  let h = Math.round((nh * w) / nw);
  if (even) {
    w -= w % 2;
    h -= h % 2;
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: !even });
  if (!ctx) throw new Error('This browser can’t draw the frames.');
  return { canvas, ctx, w, h };
}

/** A GIF of the replay so far: at most 300 pictures, 960 px wide, timed like playback at 1×. */
export async function exportGif(rec: Recording, tl: Timeline, end: number, { signal, onProgress }: ExportOptions): Promise<Blob> {
  const shots = shotsOf(rec, tl, end, GIF_SHOTS);
  if (!shots.length) throw new Error('There are no frames to export yet.');
  const frameOf = frameLoader(rec.id, shots);
  let image = await frameOf(0);
  const { ctx, w, h } = canvasFor(image, GIF_WIDTH, false);
  const gif = new GifEncoder(w, h, KEEP);
  for (let k = 0; k < shots.length; k++) {
    if (signal.aborted) throw abortError();
    // A frame that didn't load leaves the last one up.
    if (k > 0) image = (await frameOf(k)) ?? image;
    drawPicture(ctx, w, h, pictureOf(rec, tl, shots[k], image));
    gif.addFrame(ctx.getImageData(0, 0, w, h).data, shots[k].holdMs);
    onProgress((k + 1) / shots.length);
    // Let the page breathe between frames.
    await sleep(0);
  }
  return gif.finish();
}

const VIDEO_TYPES = ['video/mp4;codecs=avc1.42E01E', 'video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];

/** The best video type this browser records a canvas in, MP4 first; null when it can't. */
export function videoType(): string | null {
  if (typeof MediaRecorder === 'undefined' || typeof HTMLCanvasElement === 'undefined' || typeof HTMLCanvasElement.prototype.captureStream !== 'function') return null;
  return VIDEO_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? null;
}

/**
 * A video of the replay so far, recorded off a canvas in real time. A long
 * run plays faster so the recording takes about a minute at most; the tab
 * must stay visible meanwhile, since hidden tabs slow their timers.
 */
export async function exportVideo(rec: Recording, tl: Timeline, end: number, { signal, onProgress }: ExportOptions): Promise<{ blob: Blob; ext: 'mp4' | 'webm' }> {
  const type = videoType();
  if (!type) throw new Error('This browser can’t record video. Try the GIF instead.');
  const shots = shotsOf(rec, tl, end, VIDEO_SHOTS);
  if (!shots.length) throw new Error('There are no frames to export yet.');
  const total = shots.reduce((sum, s) => sum + s.holdMs, 0);
  const pace = Math.max(1, total / VIDEO_TARGET_MS);
  const frameOf = frameLoader(rec.id, shots);
  let image = await frameOf(0);
  const { canvas, ctx, w, h } = canvasFor(image, VIDEO_WIDTH, true);
  let picture = pictureOf(rec, tl, shots[0], image);
  drawPicture(ctx, w, h, picture);

  const stream = canvas.captureStream(30);
  const recorder = new MediaRecorder(stream, { mimeType: type, videoBitsPerSecond: 6_000_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
  });
  recorder.start(1000);
  try {
    let played = 0;
    for (let k = 0; k < shots.length; k++) {
      if (k > 0) {
        image = (await frameOf(k)) ?? image;
        picture = pictureOf(rec, tl, shots[k], image);
      }
      // At least two video frames each, so nothing flashes by unseen.
      const until = performance.now() + Math.max(70, shots[k].holdMs / pace);
      do {
        if (signal.aborted) throw abortError();
        // Redraw while holding: a canvas stream only sends frames when the canvas is painted.
        drawPicture(ctx, w, h, picture);
        await sleep(Math.min(66, Math.max(0, until - performance.now())));
      } while (performance.now() < until);
      played += shots[k].holdMs;
      onProgress(played / total);
    }
  } finally {
    if (recorder.state !== 'inactive') recorder.stop();
    stream.getTracks().forEach((t) => t.stop());
  }
  await stopped;
  const recorded = recorder.mimeType || type;
  return { blob: new Blob(chunks, { type: recorded.split(';')[0] }), ext: recorded.includes('mp4') ? 'mp4' : 'webm' };
}

/** A file name for an export, from the recording's name or else its id. */
export function exportName(rec: Recording): string {
  const slug = (rec.name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/, '');
  return slug || rec.id;
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking at once can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** Entries the live stream sent, added after those already here (any repeat is dropped). */
export function appendEntries(rec: Recording, batch: RecordingEntry[]): Recording {
  let last = rec.entries.length ? rec.entries[rec.entries.length - 1].seq : -Infinity;
  const fresh: RecordingEntry[] = [];
  let frames = 0;
  for (const e of batch) {
    if (e.seq <= last) continue;
    last = e.seq;
    fresh.push(e);
    if (hasFrame(e)) frames++;
  }
  if (!fresh.length) return rec;
  return { ...rec, entries: [...rec.entries, ...fresh], frames: rec.frames + frames };
}
